import type { Escaner } from "./port";
import { openEnvironmentCamera, stopCameraStream } from "./cameraStream";

// ADR-012: ÚNICO archivo autorizado a importar `zxing-wasm` — la prueba de
// aislamiento (`web/test/validator/scanner-isolation.test.ts`) falla si
// aparece en cualquier otro. El import es `await import(...)` diferido
// DENTRO de `iniciar`, nunca en el nivel superior del módulo: un navegador
// con `BarcodeDetector` nativo (`selectScanner.ts` lo prioriza) jamás
// instancia este adaptador y por lo tanto nunca descarga el WASM.
const SCAN_INTERVAL_MS = 200;

export function createWasmScanner(): Escaner {
  let stream: MediaStream | null = null;
  let intervalId: ReturnType<typeof setInterval> | null = null;
  let canvas: HTMLCanvasElement | null = null;
  let decoding = false;

  return {
    disponible(): boolean {
      return Boolean(navigator.mediaDevices?.getUserMedia);
    },
    async iniciar(video, alDetectar) {
      const { readBarcodes } = await import("zxing-wasm/reader");

      stream = await openEnvironmentCamera(video);

      canvas = document.createElement("canvas");
      const context = canvas.getContext("2d", { willReadFrequently: true });

      intervalId = setInterval(() => {
        if (decoding || !canvas || !context || !video.videoWidth) return;
        decoding = true;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const frame = context.getImageData(0, 0, canvas.width, canvas.height);
        readBarcodes(frame, { formats: ["QRCode"], maxNumberOfSymbols: 1 })
          .then((results) => {
            const first = results[0];
            if (first) {
              alDetectar(first.text);
            }
          })
          .catch(() => {
            // Un frame ilegible no es un error: se reintenta en el
            // próximo ciclo.
          })
          .finally(() => {
            decoding = false;
          });
      }, SCAN_INTERVAL_MS);
    },
    detener() {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
      stopCameraStream(stream);
      stream = null;
      canvas = null;
    },
  };
}
